import React from "react";
import { StyleSheet, View } from "react-native";
import { CheckCircle2 } from "lucide-react";
import Pill from "../../../common/Pill/Pill";
import CustomText from "../../../common/general/CustomText/CustomText";
import type { ConnectorApprovalSummary } from "../catalog.utils";
import { PILL_TYPES } from "./ConnectorDetails";
import { InfoCard } from "./InfoCard";

interface AppApprovalApprovedProps {
  approval: ConnectorApprovalSummary;
}

const getApprovalSummaryText = (approval: ConnectorApprovalSummary): string => {
  if (approval.approvedBy && approval.approvedAt) {
    return `Approved by ${approval.approvedBy} on ${approval.approvedAt}`;
  }

  if (approval.approvedAt) {
    return `Approved on ${approval.approvedAt}`;
  }

  return "Approved";
};

function AppApprovalApproved({ approval }: AppApprovalApprovedProps) {
  return (
    <>
      <Pill
        text={getApprovalSummaryText(approval)}
        backgroundColor={PILL_TYPES.approved.backgroundColor}
        textColor={PILL_TYPES.approved.textColor}
        textSize="xs"
        updated={true}
        borderColor={PILL_TYPES.approved.textColor}
        borderWidth={1}
        icon={<CheckCircle2 size={16} color={PILL_TYPES.approved.textColor} />}
        containerStyle={{
          borderRadius: 16,
          paddingHorizontal: 12,
          paddingVertical: 12,
          alignSelf: "flex-start",
          marginTop: 20,
          width: "100%",
        }}
        textStyle={{ marginLeft: 4 }}
      />

      <View style={styles.statBox}>
        <CustomText
          text={approval?.userConnections || 0}
          size="xxl"
          useTranslationText={false}
          textType="text"
          weight="bold"
          role="text"
          style={{ color: "#101828" }}
          numberOfLines={1}
        />
        <CustomText
          text="users have connected their personal accounts"
          size="xs"
          useTranslationText={false}
          textType="text"
          weight="normal"
          role="text"
          style={{ color: "#667085", marginTop: 2 }}
        />
      </View>

      <CustomText
        text="Individual users connect their own accounts in their personal settings. You approved the app and they authorize access to their data."
        size="xs"
        useTranslationText={false}
        textType="text"
        weight="normal"
        role="text"
        style={{ color: "#667085", marginTop: 20 }}
      />
    </>
  );
}

interface AppApprovalSetupProps {
  connectorName: string;
}

function AppApprovalSetup({ connectorName }: AppApprovalSetupProps) {
  return (
    <>
      <CustomText
        text={`Approving registers Axio as a trusted app for your organization. Users can then connect their personal ${connectorName} accounts in their settings.`}
        size="s"
        useTranslationText={false}
        textType="text"
        weight="normal"
        role="text"
        style={{ color: "#344054", marginTop: 20 }}
      />
      <InfoCard
        title="How app approval works"
        items={[
          "You approve Axio as a trusted app for your organization",
          `Faculty and students connect their own ${connectorName} in personal settings`,
          "Each user authorizes access to their own files. Axio never accesses data they have not granted.",
        ]}
      />
    </>
  );
}

interface AppApprovalContentProps {
  connectorName: string;
  approval: ConnectorApprovalSummary | null;
}

export function AppApprovalContent({ connectorName, approval }: AppApprovalContentProps) {
  if (approval) {
    return <AppApprovalApproved approval={approval} />;
  }
  return <AppApprovalSetup connectorName={connectorName} />;
}

const styles = StyleSheet.create({
  statBox: {
    backgroundColor: "#F9FAFB",
    borderRadius: 10,
    padding: 16,
    marginTop: 20,
  },
});
